import { Component, ViewChild, ElementRef } from "@angular/core";
import { NavController, ModalController } from "ionic-angular";
import {AngularFire, FirebaseListObservable, FirebaseObjectObservable} from "angularfire2";
import { FileService } from "../../providers/file";
import { SettingsService } from "../../providers/settings";
import { UiService } from "../../providers/ui";
import { Value } from "../value/value";
import { Health } from "../health/health";
import { Emotion } from "../emotion/emotion";
import { map } from "lodash";
import moment from "moment";

@Component({
  selector: "page-settings",
  templateUrl: "settings.html",
})
export class Settings {
  @ViewChild("csvInput") csvInput: ElementRef;

  // Loader
  loader: any = null;

  // Data
  segmentModel: string = "Values";

  isValuesLoaded: boolean = false;
  valuesSubscription: any = null;
  values: FirebaseListObservable<any[]>;

  isHealthLoaded: boolean = false;
  healthSubscription: any = null;
  health: FirebaseListObservable<any[]>;

  isEmotionsLoaded: boolean = false;
  emotionsSubscription: any = null;
  emotions: FirebaseListObservable<any[]>;
  //searchResults: Observable<any[]>;

  maintenance: boolean = false;
  postSettings: any;
  whitelistedEmails: any;

  notificationSettings: any;

  constructor(
    public navCtrl: NavController,
    public modalCtrl: ModalController,
    public af: AngularFire,
    public fileService: FileService,
    public settingsService: SettingsService,
    public uiService: UiService,
  ) {
    this.values = this.af.database.list("/values");
    this.valuesSubscription = this.values.subscribe(data => {
      this.isValuesLoaded = true;
      //this.search( { target: { value: '*' } } );
    });

    this.health = this.af.database.list("/health");
    this.healthSubscription = this.health.subscribe(data => {
      this.isHealthLoaded = true;
    });

    this.emotions = this.af.database.list("/emotions");
    this.emotionsSubscription = this.emotions.subscribe(data => {
      this.isEmotionsLoaded = true;
    });

    this.postSettings = {
      badWords: this.af.database.list("/settings/post/bad-words"),
      alertPhrases: this.af.database.list("/settings/post/alert-phrases"),
      alertMessage: {
        title_en: "",
        title_trm: "",
        content_en: "",
        content_trm: "",
        visible: false,
      },
    };

    this.whitelistedEmails = {
      list: null,
      searchResults: null,
      subscription: this.af.database
        .list("/settings/whitelisted-emails")
        .subscribe(whitelistedEmails => {
          this.whitelistedEmails.list = whitelistedEmails;
        }),
      tempModel: {
        email: "",
        school: ""
      },
    };

    this.af.database.object("/settings/maintenance").subscribe(data => {
      if (!data.$exists()) {
        this.maintenance = false;

        return null;
      }

      this.maintenance = data.hasOwnProperty("active") ? data.active : false;
    });

    this.notificationSettings = {
      defaults: this.af.database.object("/settings/notifications/defaults"),
      current: {
        articles: {
          article_title: "",
          article_body_mobile: "",
          article_body_email: "",
        },
        inactivity: {
          inactivity_enabled: true,
          inactivity_title: "",
          inactivity_body_mobile: "",
          inactivity_body_email: "",
          inactivity_7_day_notification: "",
          inactivity_14_day_notification: ""
        }
      }
    }
  }

  // Lifecycle hooks
  ionViewWillEnter() {
    this.af.database
      .object("/settings/post/alert-message")
      .subscribe(alertMessage => {
        this.postSettings.alertMessage.title_en = alertMessage.title_en;
        this.postSettings.alertMessage.title_trm = alertMessage.title_trm;
        this.postSettings.alertMessage.content_en = alertMessage.content_en;
        this.postSettings.alertMessage.content_trm = alertMessage.content_trm;
        this.postSettings.alertMessage.visible = true;
      });
    this.notificationSettings.defaults.subscribe(defaults => {
      this.notificationSettings.current.articles.article_title = defaults.article_title;
      this.notificationSettings.current.articles.article_body_mobile = defaults.article_body_mobile;
      this.notificationSettings.current.articles.article_body_email = defaults.article_body_email;
      this.notificationSettings.current.inactivity.inactivity_enabled = defaults.inactivity_enabled;
      this.notificationSettings.current.inactivity.inactivity_title = defaults.inactivity_title;
      this.notificationSettings.current.inactivity.inactivity_body_mobile = defaults.inactivity_body_mobile;
      this.notificationSettings.current.inactivity.inactivity_body_email = defaults.inactivity_body_email;
      this.notificationSettings.current.inactivity.inactivity_7_day_notification = defaults.inactivity_7_day_notification;
      this.notificationSettings.current.inactivity.inactivity_14_day_notification = defaults.inactivity_14_day_notification;
    })
  }

  // Event handlers
  onLogout() {
    this.valuesSubscription.unsubscribe();
    this.healthSubscription.unsubscribe();
    this.emotionsSubscription.unsubscribe();
    this.af.auth.logout();
  }

  onValue(valueObject?: any) {
    if (valueObject) {
      valueObject.id = valueObject.$key;
    }

    let editModal = this.modalCtrl.create(Value, {
      value: valueObject ? valueObject : {},
      edit: valueObject ? true : false,
    });
    editModal.present();
  }

  onHealth(levelObject?: any) {
    if (levelObject) {
      levelObject.id = levelObject.$key;
    }

    let editModal = this.modalCtrl.create(Health, {
      level: levelObject ? levelObject : {},
      edit: levelObject ? true : false,
    });
    editModal.present();
  }

  onEmotion(emotionObject?: any) {
    if (emotionObject) {
      emotionObject.id = emotionObject.$key;
    }

    let editModal = this.modalCtrl.create(Emotion, {
      emotion: emotionObject ? emotionObject : {},
      edit: emotionObject ? true : false,
    });
    editModal.present();
  }

  onMaintenanceMode(event: any) {
    let maintenance = {
      active: false,
      lastUpdated: moment().unix(),
    };

    if (event.checked) {
      maintenance.active = true;
    }

    return this.af.database
      .object("/settings/maintenance")
      .update(maintenance)
      .then(() => {
        // Record update history
        this.af.database
          .list("/settings/maintenance/history")
          .push(maintenance);
      });
  }

  onSaveAlertMessage() {
    return this.settingsService
      .updateAlertMessage(this.postSettings.alertMessage)
      .then(() => {
        return this.uiService.showToast("Alert message updated successfully");
      })
      .catch(error => {
        return this.uiService.showAlert(
          "Whoops!",
          error.message ||
            "Email could not be save the alert message, please try again",
        );
      });
  }

  onSearchWhitelistedEmails(event: any) {
    let email = event.target.value;
    if (!email || email.trim() === "") {
      this.whitelistedEmails.searchResults = null;

      return null;
    }

    email = email.toLowerCase();

    this.whitelistedEmails.searchResults = this.whitelistedEmails.list.filter(
      whitelistedEmail => {
        if (!whitelistedEmail.email || whitelistedEmail.email.trim() === "") {
          return false;
        }

        return whitelistedEmail.email.toLowerCase().includes(email);
      },
    );

    this.whitelistedEmails.emptyResults =
      this.whitelistedEmails.searchResults.length > 0;
  }


  // Search helpers
  resetSearch() {
    this.whitelistedEmails.searchQuery = "";
    this.whitelistedEmails.searchResults = null;
  }

  // Whitelist helpers
  triggerBatchUploadWhiteListedEmails() {
    return this.csvInput.nativeElement.click();
  }

  downloadWhitelistEmailCsvTemplate($event) {
    $event.preventDefault();

    return this.fileService.createAndDownloadFile(
      "whitelist-emails.csv",
      "text/csv",
      'lisa@example.com,"School Name"\nharry@example.com,"School Name"',
    );
  }

  batchUploadWhiteListedEmails(event: any): Promise<any> {
    return this.settingsService
      .batchUploadWhiteListedEmails(event)
      .then((whitelistedEmails: any) => {
        // console.log("Bulk adding whitelisted emails", whitelistedEmails);
        this.csvInput.nativeElement.value = "";

        return this.uiService.showToast(
          "Whitelist emails uploaded successfully",
        );
      })
      .catch(error => {
        return this.uiService.showAlert(
          "Whoops!",
          error.message || "Could not process CSV file, please try again",
        );
      });
  }

  addWhiteListedEmail() {
    return this.settingsService
      .addWhiteListedEmail(this.whitelistedEmails.tempModel)
      .then(() => {
        this.whitelistedEmails.tempModel.email = "";
        this.whitelistedEmails.tempModel.school = "";
        this.resetSearch();

        return this.uiService.showToast(
          "Email added to whitelist successfully",
        );
      })
      .catch(error => {
        return this.uiService.showAlert(
          "Whoops!",
          error.message ||
            "Email could not be added to the whitelist, please try again",
        );
      });
  }

  removeWhiteListedEmail(userDetails) {
    if (
      !confirm("Are you sure you want to remove this email from the Whitelist?")
    ) {
      return null;
    }

    return this.settingsService
      .removeWhiteListedEmail(userDetails)
      .then(() => {
        this.resetSearch();

        return this.uiService.showToast("Email removed from whitelist");
      })
      .catch(error => {
        return this.uiService.showAlert(
          "Whoops!",
          error.message ||
            "Email could not be added to the whitelist, please try again",
        );
      });
  }

  // Alert phrase helpers
  addAlertPhrase(alertPhrase: string = "New alert phrase") {
    return this.settingsService.addAlertPhrase(alertPhrase).then(() => {
      return this.uiService.showToast("Added new alert phrase");
    });
  }

  updateAlertPhrase(alertPhrase: any) {
    return this.settingsService
      .updateAlertPhrase(alertPhrase)
      .then(() => {
        return this.uiService.showToast("Alert phrase updated successfully");
      })
      .catch(error => {
        return this.uiService.showAlert(
          "Whoops!",
          error.message || "Email could update alert phrase, please try again",
        );
      });
  }

  removeAlertPhrase(alertPhrase: any) {
    if (!confirm("Are you sure you want to remove this alert phrase?")) {
      return null;
    }

    return this.settingsService.removeAlertPhrase(alertPhrase).then(() => {
      return this.uiService.showToast("Alert phrase removed successfully");
    });
  }

  // Bad words
  addBadWord(badWord: string = "New bad word") {
    return this.settingsService.addBadWord(badWord);
  }

  updateBadWord(badWord: any) {
    return this.settingsService
      .updateBadWord(badWord)
      .then(() => {
        return this.uiService.showToast("Bad word updated successfully");
      })
      .catch(error => {
        return this.uiService.showAlert(
          "Whoops!",
          error.message || "Email could update bad word, please try again",
        );
      });
  }

  removeBadWord(badWord: any) {
    if (!confirm("Are you sure you want to remove this bad word?")) {
      return null;
    }

    return this.settingsService.removeBadWord(badWord).then(() => {
      return this.uiService.showToast("Bad word removed successfully");
    });
  }

  updateNotificationSettings(type: any) {
    let updates;
    if (type === 'article') {
      updates = this.notificationSettings.current.articles;
    }
    if (type === 'inactivity') {
      updates = this.notificationSettings.current.inactivity;
    }

    return this.settingsService
      .updateNotificationDefaults(updates)
      .then(() => {
        return this.uiService.showToast("Updated successfully.");
      })
      .catch(error => {
        return this.uiService.showAlert(
          "Whoops!",
          error.message || "Could not update default values, please try again",
        );
      })
  }
}



// WEBPACK FOOTER //
// ./src/pages/settings/settings.ts